import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { AssayEnvelopeBrowserService, type AssayEnvelopeFileOut } from '@/api'
import { DEFAULT_TREE_KIND, type TreeKind } from '@/types/extractions'
import { extractErrorMessage } from '@/utils/apiError'

function projectKey(projectId: number): string {
  return String(projectId)
}

function fileKey(projectId: number, fileId: string, treeKind: TreeKind): string {
  return `${projectId}:${treeKind}:${fileId}`
}

function deleteProjectEntries<T>(cache: Map<string, T>, projectId: number): void {
  const prefix = `${projectId}:`
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) cache.delete(key)
  }
}

function invalidateProjectGenerations(cache: Map<string, number>, projectId: number): void {
  const prefix = `${projectId}:`
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) cache.set(key, (cache.get(key) ?? 0) + 1)
  }
}

export const useAssayEnvelopeWorkspaceStore = defineStore('assayEnvelopeWorkspace', () => {
  const availability = ref<Map<string, boolean>>(new Map())
  const availabilityReasons = ref<Map<string, string | null>>(new Map())
  const availabilityModes = ref<Map<string, 'native' | null>>(new Map())
  const nativeAuthorities = ref<Map<string, boolean>>(new Map())
  const files = ref<Map<string, AssayEnvelopeFileOut>>(new Map())
  const availabilityGeneration = new Map<string, number>()
  const fileGeneration = new Map<string, number>()
  let cacheEpoch = 0

  function clearProject(projectId: number): void {
    deleteProjectEntries(files.value, projectId)
    invalidateProjectGenerations(fileGeneration, projectId)
  }

  function reset(): void {
    cacheEpoch += 1
    availability.value.clear()
    availabilityReasons.value.clear()
    availabilityModes.value.clear()
    nativeAuthorities.value.clear()
    files.value.clear()
    availabilityGeneration.clear()
    fileGeneration.clear()
  }

  function availableFor(projectId: number) {
    return computed(() => availability.value.get(projectKey(projectId)) ?? false)
  }

  function availabilityReasonFor(projectId: number) {
    return computed(() => availabilityReasons.value.get(projectKey(projectId)) ?? null)
  }

  function availabilityModeFor(projectId: number) {
    return computed(() => availabilityModes.value.get(projectKey(projectId)) ?? null)
  }

  function fileById(projectId: number, fileId: string, treeKind: TreeKind = DEFAULT_TREE_KIND) {
    return computed(() => files.value.get(fileKey(projectId, fileId, treeKind)) ?? null)
  }

  async function refreshAvailability(projectId: number): Promise<boolean> {
    const epoch = cacheEpoch
    const key = projectKey(projectId)
    const generation = (availabilityGeneration.get(key) ?? 0) + 1
    availabilityGeneration.set(key, generation)
    try {
      const result =
        await AssayEnvelopeBrowserService.getAvailabilityProjectsProjectIdAssayEnvelopesAvailabilityGet(
          { projectId },
        )
      if (epoch !== cacheEpoch || availabilityGeneration.get(key) !== generation) return false
      const mode = result.mode === 'native' ? 'native' : null
      availability.value.set(key, result.available)
      availabilityReasons.value.set(key, result.reason ?? null)
      availabilityModes.value.set(key, mode)
      nativeAuthorities.value.set(key, result.native_authority ?? mode === 'native')
      if (!result.available) clearProject(projectId)
      return result.available
    } catch (error) {
      if (epoch === cacheEpoch && availabilityGeneration.get(key) === generation) {
        availability.value.set(key, false)
        availabilityReasons.value.set(key, extractErrorMessage(error))
        availabilityModes.value.set(key, null)
        clearProject(projectId)
      }
      throw error
    }
  }

  async function resolveLegacy(
    projectId: number,
    extractionId: string,
    certSlug: string,
    treeKind: TreeKind,
  ) {
    const epoch = cacheEpoch
    const result =
      await AssayEnvelopeBrowserService.resolveLegacyProjectsProjectIdAssayEnvelopesResolveLegacyGet(
        {
          projectId,
          extractionId,
          certSlug,
          treeKind,
        },
      )
    if (epoch !== cacheEpoch)
      throw new Error('Your session changed. Reload this extraction bookmark.')
    return result
  }

  async function fetchFileByIdOrThrow(
    projectId: number,
    fileId: string,
    treeKind: TreeKind = DEFAULT_TREE_KIND,
  ): Promise<AssayEnvelopeFileOut> {
    const epoch = cacheEpoch
    const key = fileKey(projectId, fileId, treeKind)
    const generation = (fileGeneration.get(key) ?? 0) + 1
    fileGeneration.set(key, generation)
    const file =
      await AssayEnvelopeBrowserService.getFileProjectsProjectIdAssayEnvelopesFilesFileIdGet({
        projectId,
        fileId,
        treeKind,
      })
    if (epoch !== cacheEpoch || fileGeneration.get(key) !== generation) {
      throw new Error('The assay file request was superseded by newer navigation.')
    }
    files.value.set(key, file)
    return file
  }

  return {
    availableFor,
    availabilityReasonFor,
    availabilityModeFor,
    resolveLegacy,
    fileById,
    refreshAvailability,
    fetchFileByIdOrThrow,
    reset,
  }
})
